import { useEffect, useState } from 'react'
import type { ApiClient, ReportView } from '../api/client'
import { describeError, type Problem } from '../i18n/errors'
import { ProblemLine } from '../ProblemLine'
import { nextStep, readinessReason } from '../i18n/derived'
import { plural, t } from '../i18n/dict'
import { when } from '../i18n/format'
import { formats } from '../i18n/locale'
import { ModeTag } from './Rules'

/** The observe-week review. Same queries and same verdict as the TUI and guardianctl — only
 * the rendering differs. The judgement is stated rather than left to be inferred from counts:
 * an idle week produces a clean-looking table, and that is exactly the trap. */
export function Report({ api, account }: { api: ApiClient; account: number }) {
  const [selected, setDays] = useState(7)
  const [view, setView] = useState<ReportView | null>(null)
  const [error, setError] = useState<Problem | null>(null)

  useEffect(() => {
    setView(null)
    api.report(account, selected).then(setView).catch((e: unknown) => setError(describeError(e)))
  }, [api, account, selected])

  if (error && !view) return <ProblemLine problem={error} />
  if (!view) return <p className="muted">{t('report.reading')}</p>

  const a = view.activity
  const dash = (n: number) => (n === 0 ? <span className="muted">{t('unit.none')}</span> : formats().integer.format(n))
  const days = (n: number) => plural('common.days', n, { count: formats().integer.format(n) })

  return (
    <section className="console">
      <p>
        <strong>{t('common.accountN', { id: view.account })}</strong>{' '}
        <span className="muted">{t('common.window', { days: days(view.days), until: when(view.until) })}</span>
        <select
          value={selected}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label={t('common.daysAria')}
          className="days"
        >
          {[7, 14, 30].map((d) => (
            <option key={d} value={d}>
              {days(d)}
            </option>
          ))}
        </select>
      </p>

      <dl className="grid">
        <dt>{t('report.dt.opened')}</dt>
        <dd>{formats().integer.format(a.opened)}</dd>
        <dt>{t('report.dt.closed')}</dt>
        <dd>{formats().integer.format(a.closed)}</dd>
        <dt>{t('report.dt.pending')}</dt>
        <dd>{formats().integer.format(a.pending)}</dd>
        <dt>{t('report.dt.ticks')}</dt>
        <dd>{formats().integer.format(a.ticks)}</dd>
        {a.latencySamples > 0 && (
          <>
            <dt>{t('report.dt.latency')}</dt>
            <dd>
              {t('report.latency', {
                p50: formats().integer.format(a.latencyP50),
                p90: formats().integer.format(a.latencyP90),
                max: formats().integer.format(a.latencyMax),
              })}{' '}
              <span className="muted">{t('report.samples', { n: formats().integer.format(a.latencySamples) })}</span>
            </dd>
          </>
        )}
      </dl>

      {view.verdicts.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('report.th.rule')}</th>
                <th>{t('report.th.mode')}</th>
                <th>{t('report.th.fired')}</th>
                <th>{t('report.th.targets')}</th>
                <th>{t('report.th.days')}</th>
                <th>{t('report.th.suppressed')}</th>
                <th>{t('report.th.verdict')}</th>
              </tr>
            </thead>
            <tbody>
              {view.verdicts.map((v) => (
                <tr key={v.rule}>
                  <td>
                    <code>{v.rule}</code>
                  </td>
                  <td>
                    <ModeTag mode={v.mode} />
                  </td>
                  <td>{dash(v.fired)}</td>
                  <td>{dash(v.targets)}</td>
                  <td>{dash(v.days)}</td>
                  <td>{dash(v.suppressed)}</td>
                  <td>
                    {v.readiness === 'CandidateForEnforce' ? (
                      <span className="good">{t('report.readiness.candidate')}</span>
                    ) : v.readiness === 'NeedsReview' ? (
                      <span className="warn">{t('report.readiness.review')}</span>
                    ) : (
                      <span className="mode observe">{t('report.readiness.inconclusive')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view.isIdle ? (
        <div className="panel yellow">
          <strong>{t('report.idle.title')}</strong>
          <p>
            <strong>{t('report.idle.head')}</strong>
          </p>
          <p>{t('report.idle.body')}</p>
        </div>
      ) : view.verdicts.length === 0 ? (
        <div className="panel red">
          <strong>{t('report.noRules.title')}</strong>
          <p>
            {t('report.noRules.body', {
              entries: plural('report.entries', view.entries, { count: formats().integer.format(view.entries) }),
            })}
          </p>
        </div>
      ) : (
        <ul className="verdicts">
          {view.verdicts.map((v) => (
            <li key={v.rule}>
              <strong>
                <code>{v.rule}</code>
              </strong>
              <br />
              {/* The server words this too, for the TUI. Rendered here from the same counts
                  it uses, because a queue-processing daemon has no request to read a language
                  from — see i18n/derived.ts. */}
              <span className="muted">{readinessReason(v, view.entries)}</span>
              <br />
              {nextStep(v.readiness)}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
